import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-hot-toast';


const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const orderStatuses = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'orders'));
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter orders: only show orders with successful payments
      // - For online payments (razorpay): paymentStatus must be 'paid'
      // - For COD: paymentStatus must be 'pending' (awaiting payment on delivery)
      // - Exclude orders with failed or cancelled payment status
      const validOrders = ordersData.filter(order => {
        const paymentStatus = order.paymentStatus || 'pending';
        const paymentMethod = order.paymentMethod || 'cod';

        // For razorpay, only show if payment is paid
        if (paymentMethod === 'razorpay') {
          return paymentStatus === 'paid';
        }
        // For COD, show if payment is pending (not paid yet) or pending (awaiting COD)
        return paymentStatus === 'pending' || paymentStatus === 'paid';
      });

      // Sort by creation date (newest first)
      validOrders.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      setOrders(validOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingId(orderId);
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus,
        updatedAt: new Date()
      });

      setOrders(prev => prev.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      toast.success('Order status updated successfully');
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdatingId(null);
    }
  };

  const updatePaymentStatus = async (orderId, newPaymentStatus) => {
    try {
      setUpdatingId(orderId);
      await updateDoc(doc(db, 'orders', orderId), {
        paymentStatus: newPaymentStatus,
        updatedAt: new Date()
      });

      setOrders(prev => prev.map(order =>
        order.id === orderId ? { ...order, paymentStatus: newPaymentStatus } : order
      ));

      setSelectedOrder(prev =>
        prev ? { ...prev, paymentStatus: newPaymentStatus } : null
      );

      toast.success('Payment status updated successfully');
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gold text-deep-brown',
      processing: 'bg-deep-brown text-cream',
      shipped: 'bg-warm-gray text-white',
      delivered: 'bg-sage text-white',
      cancelled: 'bg-red-600 text-white'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (paymentStatus, paymentMethod) => {
    if (paymentMethod === 'cod') {
      return 'bg-blue-100 text-blue-800';
    }
    const colors = {
      paid: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[paymentStatus] || 'bg-gray-100 text-gray-800';
  };

  const OrderDetailsModal = ({ order, onClose }) => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Order Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Order ID</p>
              <p className="text-sm text-gray-900">#{order.id.substring(0, 8)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Status</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                {order.status || 'pending'}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Payment Method</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(order.paymentStatus, order.paymentMethod)}`}>
                {order.paymentMethod === 'cod' ? 'Cash on Delivery' : order.paymentMethod === 'razorpay' ? 'Online Payment' : 'Unknown'}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Payment Status</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(order.paymentStatus, order.paymentMethod)}`}>
                {order.paymentStatus ? order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1) : 'Pending'}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Customer Email</p>
              <p className="text-sm text-gray-900">{order.customerEmail || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Total Amount</p>
              <p className="text-sm text-gray-900">₹{(order.total || 0).toLocaleString()}</p>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-sm font-medium text-gray-700 mb-2">Price Breakdown</p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="text-gray-900">₹{(order.subtotal || 0).toLocaleString()}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm bg-green-50 -mx-3 px-3 py-1.5">
                  <span className="text-green-700 font-medium">
                    Discount {order.appliedCoupon && `(${order.appliedCoupon.code})`}:
                  </span>
                  <span className="text-green-600 font-semibold">-₹{(order.discount || 0).toLocaleString()}</span>
                </div>
              )}
              {order.shipping > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping:</span>
                  <span className="text-gray-900">₹{(order.shipping || 0).toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold border-t border-gray-300 pt-1 mt-1">
                <span className="text-gray-900">Total:</span>
                <span className="text-gray-900">₹{(order.total || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {order.items && order.items.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Order Items</p>
              <div className="space-y-2">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <div>
                      <p className="text-sm font-medium">{item.title || 'Product'}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity || 1}</p>
                    </div>
                    <p className="text-sm font-medium">₹{(item.price || 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {order.shippingAddress && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Shipping Address</p>
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-sm">{order.shippingAddress.name}</p>
                <p className="text-sm">{order.shippingAddress.address}</p>
                <p className="text-sm">{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}</p>
                <p className="text-sm">{order.shippingAddress.phone}</p>
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Update Status</p>
            <select
              value={order.status || 'pending'}
              onChange={(e) => updateOrderStatus(order.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500"
            >
              {orderStatuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {order.paymentMethod === 'cod' && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Update Payment Status (COD)</p>
              <select
                value={order.paymentStatus || 'pending'}
                onChange={(e) => updatePaymentStatus(order.id, e.target.value)}
                disabled={updatingId === order.id}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
              >
                <option value="pending">Pending (Not Yet Received)</option>
                <option value="paid">Paid (Payment Received)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Update this when customer pays cash at delivery</p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8 md:pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif text-deep-brown">Orders</h1>
          <p className="text-sm md:text-base text-warm-gray font-medium">Manage customer orders and track shipments</p>
        </div>



        {/* Filters */}
        <div className="mb-4 md:mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 md:px-4 py-2 rounded-md font-medium text-xs md:text-sm ${filter === 'all'
                  ? 'bg-gold text-deep-brown'
                  : 'bg-white text-warm-gray border border-warm-gray/30'
                }`}
            >
              All Orders ({orders.length})
            </button>
            {orderStatuses.map(status => {
              const count = orders.filter(order => order.status === status).length;
              return (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 md:px-4 py-2 rounded-md font-medium text-xs md:text-sm ${filter === status
                      ? 'bg-gold text-deep-brown'
                      : 'bg-white text-warm-gray border border-warm-gray/30'
                    }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-warm-gray/20">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-warm-gray/20">
              <thead className="bg-cream">
                <tr>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider hidden sm:table-cell">
                    Customer
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider hidden md:table-cell">
                    Payment
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider hidden lg:table-cell">
                    Date
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-warm-gray/20">
                {filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm font-medium text-gray-900">
                      #{order.id.substring(0, 8)}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden sm:table-cell">
                      {order.customerEmail || 'N/A'}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                        {order.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-semibold rounded-full w-fit ${getPaymentStatusColor(order.paymentStatus, order.paymentMethod)}`}>
                          {order.paymentMethod === 'cod' ? 'COD' : order.paymentMethod === 'razorpay' ? 'Online' : 'Unknown'}
                        </span>
                        <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-semibold rounded-full w-fit ${getPaymentStatusColor(order.paymentStatus, order.paymentMethod)}`}>
                          {order.paymentStatus ? order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1) : 'Pending'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-500">
                      ₹{(order.total || 0).toLocaleString()}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden lg:table-cell">
                      {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm font-medium">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-amber-600 hover:text-amber-900 mr-2 md:mr-3 text-xs md:text-sm"
                      >
                        View
                      </button>
                      <select
                        value={order.status || 'pending'}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        disabled={updatingId === order.id}
                        className="text-xs md:text-sm border-gray-300 rounded-md focus:border-amber-500 focus:ring-amber-500 disabled:opacity-60"
                      >
                        {orderStatuses.map(status => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </option>
                        ))}
                      </select>
                      {updatingId === order.id && (
                        <span className="ml-2 inline-block align-middle animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-2 border-amber-600 border-t-transparent" aria-label="Updating" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No orders found for the selected filter.</p>
          </div>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminOrders;